import type { ApplicationConfig } from '@angular/core';
import { inject, Injector, provideZoneChangeDetection } from '@angular/core';
import { appRoutes } from './app.routes';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideTransloco,
  provideTranslocoMissingHandler,
  provideTranslocoFallbackStrategy,
} from '@jsverse/transloco';
import { environment } from '@camino-solutions/core/environment';
import { AuthConfig, provideOAuthClient } from 'angular-oauth2-oidc';
import { AuthServiceProvider, provideAuthInitializer } from '@camino-solutions/core/auth/service';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { AppState } from '@camino-solutions/core/app/state';
import { MessageService } from 'primeng/api';
import { provideGrpc, provideGrpcLogger } from '@camino-solutions/core/grpc';
import { menu } from './config/menu.config';
import { MENU_TOKEN } from '@camino-solutions/core/app/ui/app-menu';
import { provideUserInitializer } from '@camino-solutions/core/user/service';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import {
  translocoOptions,
  provideTranslocoInitializer,
  CustomTranslocoMissingHandler,
  CustomTranslocoFallbackStrategy,
} from '@camino-solutions/core/i18n';
import { localImportProcessorWorkerFactoryProvider } from './modules/local-import/local-import-processor-worker-factory.provider';
import { DialogService } from 'primeng/dynamicdialog';
import { fileInterceptor } from '@camino-solutions/file-upload/service';
import { provideSessionState } from '@camino-solutions/core/session/state';
import { providePrimeNG } from 'primeng/config';
import { CaminoPreset } from '@camino-solutions/core/theme';
import type { GrpcMessage } from '@ngx-grpc/common';

export const appConfig: ApplicationConfig = {
  providers: [
    providePrimeNG({
      theme: {
        preset: CaminoPreset,
        options: {
          darkModeSelector: '.p-dark',
        },
      },
    }),
    { provide: AuthConfig, useValue: environment.auth },
    provideAuthInitializer(),
    provideUserInitializer(),
    localImportProcessorWorkerFactoryProvider,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      // withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withHashLocation(),
      // withEnabledBlockingInitialNavigation()
      // withEnabledBlockingInitialNavigation()
      // withDebugTracing()
      // TODO remove
      withComponentInputBinding(),
      withViewTransitions(),
    ),
    provideHttpClient(withInterceptors([fileInterceptor])),
    // provideAuth(authConfig),
    provideOAuthClient(),
    provideStore(
      [AppState],
      { developmentMode: environment.isAngularDevMode, selectorOptions: {} },
      withNgxsLoggerPlugin({ logger: console, collapsed: false, disabled: true }),
      // Only load DevTools in development and local-docker environments
      ...(['development', 'local-docker'].includes(environment.name)
        ? [withNgxsReduxDevtoolsPlugin({ disabled: false })]
        : []),
      withNgxsStoragePlugin({
        keys: ['app.theme', 'app.menu', 'app.sessionPanelIsOpen'],
        namespace: 'cs',
      }),
      // withNgxsPreboot(() => {
      //   const pendingTasks = inject(ExperimentalPendingTasks);
      //   const actions$ = inject(Actions);
      //
      //   const actionToRemoveTaskFnMap = new Map<any, () => void>();
      //
      //   // Note that you don't have to unsubscribe from the actions stream in
      //   // this specific case, as we complete the actions subject when the root
      //   // view is destroyed. In server-side rendering, the root view is destroyed
      //   // immediately once the app stabilizes and its HTML is serialized.
      //   actions$.subscribe(ctx => {
      //     if (ctx.status === ActionStatus.Dispatched) {
      //       const removeTaskFn = pendingTasks.add();
      //       actionToRemoveTaskFnMap.set(ctx.action, removeTaskFn);
      //     } else {
      //       const removeTaskFn = actionToRemoveTaskFnMap.get(ctx.action);
      //       if (typeof removeTaskFn === 'function') {
      //         removeTaskFn();
      //         actionToRemoveTaskFnMap.delete(ctx.action);
      //       }
      //     }
      //   });
      // })
      // withNgxsStoragePlugin({ keys: [TODOS_STORAGE_KEY] })
    ),
    provideTransloco(translocoOptions),
    provideTranslocoMissingHandler(CustomTranslocoMissingHandler),
    provideTranslocoFallbackStrategy(CustomTranslocoFallbackStrategy),
    provideTranslocoInitializer(),
    // TODO ez kell?
    provideGrpc(),
    provideGrpcLogger({
      settings: {
        requestMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
        responseMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
      },
    }),
    provideSessionState(),
    AuthServiceProvider,
    {
      provide: MENU_TOKEN,
      useFactory: () => menu(inject(Injector)),
    },
    MessageService,
    DialogService,
    // MessageDialogService,
  ],
};
